// Categorized content hubs for /blog and /compare — heading + category filter pills +
// richer cards (corner-bracket radar frame, mono meta line, title, dek, read-arrow),
// modeled on the structure FrameQuery/WideFrame use, in ClipRadar's dark style.
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from '../pages/shell';
import { CONTENT_PAGES, type SeoPage } from './registry';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const fmtDate = (iso: string) => { const [y, m] = iso.split('-'); return `${MONTHS[Number(m) - 1] ?? ''} ${y}`; };

const categoryOf = (p: SeoPage): string =>
  p.category || (p.kind === 'blog' ? 'How-to' : /alternative/.test(p.slug) ? 'Alternatives' : 'Comparisons');

const hrefOf = (p: SeoPage) => `/${p.kind === 'compare' ? 'compare' : 'blog'}/${p.slug}`;

function readMinutes(p: SeoPage): number {
  let words = 0;
  const add = (s?: string) => { if (s) words += s.trim().split(/\s+/).length; };
  p.intro?.forEach(add);
  p.sections?.forEach((s) => { s.paras?.forEach(add); s.bullets?.forEach(add); });
  p.verdict?.paras?.forEach(add);
  p.faq?.forEach(([q, a]) => { add(q); add(a); });
  return Math.max(1, Math.round(words / 200));
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return reduced;
}

// L-shaped corner bracket (radar frame). pos = 'tl' | 'tr' | 'bl' | 'br'.
function corner(pos: string, on: boolean): CSSProperties {
  const c = on ? 'var(--signal-400)' : 'rgba(74,227,255,0.42)';
  const s: CSSProperties = { position: 'absolute', width: 11, height: 11, pointerEvents: 'none', transition: 'border-color 160ms var(--ease-out)' };
  if (pos[0] === 't') { s.top = -1; s.borderTop = `1.5px solid ${c}`; } else { s.bottom = -1; s.borderBottom = `1.5px solid ${c}`; }
  if (pos[1] === 'l') { s.left = -1; s.borderLeft = `1.5px solid ${c}`; } else { s.right = -1; s.borderRight = `1.5px solid ${c}`; }
  return s;
}

function ContentCard({ page }: { page: SeoPage }) {
  const [hov, setHov] = useState(false);
  const reduced = usePrefersReducedMotion();
  const lift = hov && !reduced;
  return (
    <Link
      to={hrefOf(page)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onFocus={() => setHov(true)}
      onBlur={() => setHov(false)}
      style={{
        position: 'relative', display: 'block', padding: '22px 24px 20px', borderRadius: 12,
        border: `1px solid ${hov ? 'rgba(74,227,255,0.35)' : 'var(--line-2)'}`,
        background: hov ? 'var(--surface-raised)' : 'var(--surface-card)',
        textDecoration: 'none', transition: 'border-color 160ms var(--ease-out), background 160ms var(--ease-out), transform 160ms var(--ease-out), box-shadow 160ms var(--ease-out)',
        transform: lift ? 'translateY(-2px)' : 'none',
        boxShadow: hov ? '0 12px 30px rgba(0,0,0,0.35)' : 'none',
      }}
    >
      {['tl', 'tr', 'bl', 'br'].map((c) => <span key={c} aria-hidden="true" style={corner(c, hov)} />)}
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-3)', display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <span style={{ color: 'var(--signal-400)' }}>{categoryOf(page)}</span>
        <span style={{ color: 'var(--fg-4)' }}>·</span>
        <span>{fmtDate(page.datePublished)}</span>
        <span style={{ color: 'var(--fg-4)' }}>·</span>
        <span>{readMinutes(page)} min read</span>
      </div>
      <h3 style={{ margin: '0 0 8px', fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, letterSpacing: 'var(--tracking-heading)', lineHeight: 1.2, color: hov ? 'var(--fg-1)' : 'var(--fg-1)' }}>
        {page.hero?.h1 || page.title}
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.6, color: 'var(--fg-3)', maxWidth: 620 }}>{page.description}</p>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--signal-400)', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        Read <span style={{ transition: 'transform 160ms var(--ease-out)', transform: lift ? 'translateX(3px)' : 'none' }}>→</span>
      </span>
    </Link>
  );
}

function CategoryHub({ kicker, title, sub, pages, tags = 'ClipRadar · footage search · post-production' }: {
  kicker: string; title: string; sub: string; pages: SeoPage[]; tags?: string;
}) {
  const cats = useMemo(() => ['All', ...Array.from(new Set(pages.map(categoryOf)))], [pages]);
  const [active, setActive] = useState('All');
  const shown = active === 'All' ? pages : pages.filter((p) => categoryOf(p) === active);

  return (
    <PageShell kicker={kicker} title={title} sub={sub} wide meta={
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>{tags}</span>
    }>
      {/* category filter pills */}
      {cats.length > 2 ? (
        <div role="tablist" aria-label="Filter by category" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '4px 0 28px', borderBottom: '1px solid var(--line-1)', paddingBottom: 20 }}>
          {cats.map((c) => {
            const on = active === c;
            return (
              <button key={c} role="tab" aria-selected={on} onClick={() => setActive(c)} className={`crw-cat-pill${on ? ' is-on' : ''}`}>
                {c}
              </button>
            );
          })}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 420px), 1fr))', gap: 16 }}>
        {shown.map((p) => <ContentCard key={p.slug} page={p} />)}
      </div>
    </PageShell>
  );
}

export function BlogIndex() {
  const pages = CONTENT_PAGES.slice().sort((a, b) => b.datePublished.localeCompare(a.datePublished));
  return <CategoryHub kicker="Blog" title="The ClipRadar blog" tags="Footage search · Premiere Pro · post-production"
    sub="Guides and honest comparisons for editors who'd rather search their footage than scrub it." pages={pages} />;
}

export function CompareIndex() {
  const pages = CONTENT_PAGES.filter((p) => p.kind === 'compare');
  return <CategoryHub kicker="Compare" title="ClipRadar vs the alternatives" tags="Honest · fact-checked · as of July 2026"
    sub="How ClipRadar stacks up against the other AI footage-search tools for editors — fairly, with every claim checked against their own pages." pages={pages} />;
}
